import React, { useMemo, useState } from 'react';
import { Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { KpiCard, SearchField } from '../../../components/design-system/admin';
import { CrmPress, PRESS_STAGES, PRESS_TYPES } from '../../../common/models/crm.model';
import { CrmBoard } from './crm-board';
import { CrmCardBody } from './crm-card';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';
import { useCrmBoard } from './use-crm-board';

const PRESS_FIELDS: CrmField[] = [
  { key: 'outlet', label: 'ADMIN_CRM.FIELDS.OUTLET' },
  { key: 'type', label: 'ADMIN_CRM.FIELDS.TYPE', type: 'select', options: PRESS_TYPES },
  { key: 'contact', label: 'ADMIN_CRM.FIELDS.JOURNALIST' },
  { key: 'email', label: 'ADMIN_CRM.FIELDS.EMAIL' },
  { key: 'phone', label: 'ADMIN_CRM.FIELDS.PHONE' },
  { key: 'country', label: 'ADMIN_CRM.FIELDS.COUNTRY', type: 'country' },
  { key: 'stage', label: 'ADMIN_CRM.FIELDS.STAGE', type: 'select', options: Object.fromEntries(PRESS_STAGES.map((s) => [s.id, s.label])) },
  { key: 'angle', label: 'ADMIN_CRM.FIELDS.ANGLE', full: true },
  { key: 'next', label: 'ADMIN_CRM.FIELDS.NEXT' },
  { key: 'due', label: 'ADMIN_CRM.FIELDS.DUE', type: 'date' },
  { key: 'tags', label: 'ADMIN_CRM.FIELDS.TAGS', full: true },
  { key: 'notes', label: 'ADMIN_CRM.FIELDS.NOTES_HISTORY', type: 'textarea' },
];

/** Press and media outreach, as a Kanban. */
export const CrmPressTab: React.FC = () => {
  const { t } = useTranslation();
  const board = useCrmBoard<CrmPress>('press', 'ADMIN_CRM.PRESS.SAVED');
  const press = board.items;
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [editing, setEditing] = useState<Partial<CrmPress> | null>(null);

  const shown = useMemo(() => {
    const needle = q.toLowerCase();
    return press
      .filter((p) => !type || p.type === type)
      .filter((p) => !needle || JSON.stringify(p).toLowerCase().includes(needle));
  }, [press, q, type]);

  const live = press.filter((p) => p.stage !== 'declined').length;
  const talking = press.filter((p) => ['discussion', 'article_planned'].includes(p.stage)).length;
  const published = press.filter((p) => p.stage === 'published').length;

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-3 gap-3">
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PRESS.KPI.LIVE')} value={live} hint={t('ADMIN_CRM.PRESS.KPI.LIVE_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PRESS.KPI.TALKING')} value={<span className="text-warning">{talking}</span>} hint={t('ADMIN_CRM.PRESS.KPI.TALKING_HINT')} />
        <KpiCard loading={board.loading} label={t('ADMIN_CRM.PRESS.KPI.PUBLISHED')} value={<span className="text-success">{published}</span>} hint={t('ADMIN_CRM.PRESS.KPI.PUBLISHED_HINT')} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={q} onChange={setQ} placeholder={t('ADMIN_CRM.PRESS.SEARCH')} className="w-full sm:w-80" />
        <NativeSelect value={type} onChange={(e) => setType(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.TYPE')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.COMMON.ALL_TYPES')}</NativeSelectOption>
          {Object.entries(PRESS_TYPES).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
        </NativeSelect>
        <span className="flex-1" />
        <Button onClick={() => setEditing({ stage: 'to_contact', type: 'magazine' })}><Plus />{t('ADMIN_CRM.PRESS.NEW')}</Button>
      </div>

      {!board.loading && (
        <CrmBoard<CrmPress>
          stages={PRESS_STAGES}
          items={shown}
          manualOrder
          onOpen={setEditing}
          onMoveStage={board.moveStage}
          onReorder={board.reorder}
          renderCard={(p) => (
            <CrmCardBody country={p.country} title={p.outlet} next={p.next} due={p.due} tags={p.tags}>
              <p className="mt-0.5 truncate text-xs text-muted-foreground">
                {PRESS_TYPES[p.type] ? t(PRESS_TYPES[p.type]) : p.type}{p.contact ? ` · ${p.contact}` : ''}
              </p>
              {p.angle && <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">{p.angle}</p>}
            </CrmCardBody>
          )}
        />
      )}

      <CrmRecordSheet<CrmPress>
        record={editing}
        fields={PRESS_FIELDS}
        required="outlet"
        title={{ create: t('ADMIN_CRM.PRESS.NEW'), edit: t('ADMIN_CRM.PRESS.EDIT') }}
        onSave={async (d) => { if (await board.save(d)) setEditing(null); }}
        onDelete={async (id) => { if (await board.remove(id)) setEditing(null); }}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
